/**
 * useTurnstile.ts — Cloudflare Turnstile invisible widget hook.
 *
 * Manages the full widget lifecycle:
 *   mount  → inject script tag (once per page) + create hidden container
 *   ready  → widget rendered; execute() becomes available
 *   execute() → start invisible challenge → resolve with token
 *   after token → widget auto-reset for reuse
 *
 * Phase 2 contract (do not break):
 *   - `execute()` always returns Promise<string> (the token).
 *   - If VITE_CLOUDFLARE_TURNSTILE_SITE_KEY is absent, execute() resolves
 *     immediately with the Cloudflare always-pass test token so dev/CI
 *     environments work without real credentials.
 *   - The hook renders NO visible UI. Phase 2 wraps this hook with its own
 *     branded overlay; the hook interface stays frozen.
 *
 * Usage:
 *   const { execute, ready } = useTurnstile();
 *   const token = await execute();          // throws on hard failure
 *   await verifyTurnstileToken(token);      // POST to /api/auth/verify-turnstile
 *   // proceed with Supabase auth
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// ── Cloudflare Turnstile global type ────────────────────────────────────────
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        params: TurnstileRenderParams,
      ) => string;
      execute: (widgetId: string, params?: { action?: string }) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    /** Populated by the Turnstile script for explicit render mode. */
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileRenderParams {
  sitekey: string;
  size: 'invisible';
  callback: (token: string) => void;
  'error-callback': (code?: string) => void;
  'expired-callback': () => void;
  'timeout-callback': () => void;
  appearance: 'interaction-only'; // show a challenge ONLY when interaction needed
  theme: 'dark';
}

/** Cloudflare published always-pass test site key. */
const CF_TEST_SITE_KEY = '1x00000000000000000000AA';

/** Sentinel returned by execute() in dev-bypass mode. */
export const TURNSTILE_DEV_BYPASS_TOKEN = 'TURNSTILE_DEV_BYPASS';

const SCRIPT_ID = 'cf-turnstile-script';
const CONTAINER_ID = 'cf-turnstile-container';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad';

export interface TurnstileHandle {
  /** True once the widget is rendered and ready to execute. */
  ready: boolean;
  /** True when execute() is in progress. */
  executing: boolean;
  /**
   * Start the Turnstile challenge and return the token.
   * Rejects with an Error on hard failure.
   */
  execute: () => Promise<string>;
}

export function useTurnstile(): TurnstileHandle {
  const siteKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY as string | undefined;
  const devBypass = !siteKey;

  const [ready, setReady]         = useState(devBypass); // bypass = always ready
  const [executing, setExecuting] = useState(false);

  const widgetIdRef  = useRef<string | null>(null);
  const resolveRef   = useRef<((token: string) => void) | null>(null);
  const rejectRef    = useRef<((err: Error) => void) | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // ── Callbacks registered with Turnstile ─────────────────────────────────
  const handleToken = useCallback((token: string) => {
    setExecuting(false);
    const resolve = resolveRef.current;
    resolveRef.current  = null;
    rejectRef.current   = null;
    resolve?.(token);
    // Reset so the widget can be reused for subsequent submissions.
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const handleError = useCallback((code?: string) => {
    setExecuting(false);
    const reject = rejectRef.current;
    resolveRef.current = null;
    rejectRef.current  = null;
    reject?.(new Error(`Turnstile challenge failed (${code ?? 'unknown'})`));
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const handleExpired = useCallback(() => {
    // Token expired before execute() resolved — reset and let the next
    // execute() call start a fresh challenge.
    setExecuting(false);
    const reject = rejectRef.current;
    resolveRef.current = null;
    rejectRef.current  = null;
    reject?.(new Error('Turnstile token expired. Please try again.'));
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  // ── Widget render ────────────────────────────────────────────────────────
  const renderWidget = useCallback(() => {
    if (!window.turnstile || !containerRef.current || widgetIdRef.current) return;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey ?? CF_TEST_SITE_KEY,
      size: 'invisible',
      appearance: 'interaction-only',
      theme: 'dark',
      callback: handleToken,
      'error-callback': handleError,
      'expired-callback': handleExpired,
      'timeout-callback': handleExpired,
    });
    setReady(true);
  }, [siteKey, handleToken, handleError, handleExpired]);

  // ── Mount: inject hidden container + load script ─────────────────────────
  useEffect(() => {
    if (devBypass) return; // no DOM work needed in dev-bypass mode

    // Hidden container — appended to body so it's always in the DOM.
    if (!document.getElementById(CONTAINER_ID)) {
      const div = document.createElement('div');
      div.id = CONTAINER_ID;
      div.style.cssText =
        'position:absolute;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;';
      document.body.appendChild(div);
      containerRef.current = div;
    } else {
      containerRef.current = document.getElementById(CONTAINER_ID) as HTMLDivElement;
    }

    // If Turnstile script is already loaded, render immediately.
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Otherwise, inject the script and render when it fires the onload callback.
    if (!document.getElementById(SCRIPT_ID)) {
      window.onTurnstileLoad = () => {
        renderWidget();
      };
      const script = document.createElement('script');
      script.id    = SCRIPT_ID;
      script.src   = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        console.error('[useTurnstile] Failed to load Turnstile script');
        // Fail open — let auth proceed without Turnstile on script load failure.
        setReady(true);
      };
      document.head.appendChild(script);
    } else if (!widgetIdRef.current) {
      // Script tag exists but widget not yet rendered (e.g. component remounted).
      if (window.turnstile) renderWidget();
      else window.onTurnstileLoad = renderWidget;
    }

    return () => {
      // Remove the widget on unmount but keep the script and container — they
      // are shared across pages. Phase 2 relies on this shared lifecycle.
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        setReady(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devBypass]);

  // ── execute() ────────────────────────────────────────────────────────────
  const execute = useCallback((): Promise<string> => {
    // Dev bypass — return immediately without touching Turnstile.
    if (devBypass) {
      if (import.meta.env.DEV) {
        console.info(
          '[useTurnstile] VITE_CLOUDFLARE_TURNSTILE_SITE_KEY not set — ' +
            'returning dev-bypass token',
        );
      }
      return Promise.resolve(TURNSTILE_DEV_BYPASS_TOKEN);
    }

    // Widget not yet ready — try rendering inline if Turnstile is loaded.
    if (!widgetIdRef.current) {
      if (window.turnstile && containerRef.current) {
        renderWidget();
      }
      if (!widgetIdRef.current) {
        return Promise.reject(new Error('Turnstile widget not ready. Please try again.'));
      }
    }

    return new Promise<string>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current  = reject;
      setExecuting(true);
      window.turnstile!.execute(widgetIdRef.current!);
    });
  }, [devBypass, renderWidget]);

  return { ready, executing, execute };
}
